// src/services/accountDataService.js
import { getAuth, signOut } from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

const DELETE_ACCOUNT_URL =
  "https://europe-west3-edens-ernte-2f682.cloudfunctions.net/deleteMyAccountHttp";

const withTimeout = (promise, ms = 30000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error("timeout"));
      }, ms);
    }),
  ]);
};

const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const waitForWebFirebaseUser = async () => {
  const auth = getAuth();

  if (auth.currentUser && typeof auth.currentUser.getIdToken === "function") {
    return auth.currentUser;
  }

  return await new Promise((resolve) => {
    let unsubscribe = null;

    const timeout = setTimeout(() => {
      if (typeof unsubscribe === "function") unsubscribe();
      resolve(null);
    }, 2500);

    unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      clearTimeout(timeout);
      if (typeof unsubscribe === "function") unsubscribe();
      resolve(firebaseUser || null);
    });
  });
};

const getIdTokenForDelete = async (passedUser = null) => {
  // 1. Falls wirklich ein Firebase-Web-User übergeben wurde
  if (passedUser && typeof passedUser.getIdToken === "function") {
    const token = await passedUser.getIdToken(true);
    if (token) return token;
  }

  // 2. Web Firebase Auth direkt
  const webUser = await waitForWebFirebaseUser();

  if (webUser && typeof webUser.getIdToken === "function") {
    const token = await webUser.getIdToken(true);
    if (token) return token;
  }

  // 3. Native Capacitor Firebase Auth
  if (isNativeApp()) {
    try {
      const current = await FirebaseAuthentication.getCurrentUser();

      if (!current?.user) {
        throw new Error("native-current-user-missing");
      }

      const result = await FirebaseAuthentication.getIdToken({
        forceRefresh: true,
      });

      const nativeToken = result?.token || "";

      if (nativeToken) return nativeToken;

      throw new Error("native-id-token-empty");
    } catch (error) {
      console.warn("Native Firebase Auth Token konnte nicht geladen werden:", {
        message: error?.message,
        code: error?.code,
        full: error,
      });
    }
  }

  throw new Error("missing-firebase-user");
};

export const clearLocalAppState = () => {
  try {
    localStorage.clear();
  } catch (error) {
    console.warn("localStorage konnte nicht gelöscht werden:", error);
  }

  try {
    sessionStorage.clear();
  } catch (error) {
    console.warn("sessionStorage konnte nicht gelöscht werden:", error);
  }
};

export const signOutEverywhere = async () => {
  const errors = [];

  try {
    const auth = getAuth();
    await signOut(auth);
  } catch (error) {
    errors.push(error);
  }

  try {
    if (isNativeApp()) {
      await FirebaseAuthentication.signOut();
    }
  } catch (error) {
    errors.push(error);
  }

  clearLocalAppState();

  if (errors.length > 0) {
    console.warn("signOutEverywhere teilweise fehlgeschlagen:", errors);
  }

  return true;
};

export const deleteMyAccountData = async (passedUser = null) => {
  const idToken = await getIdTokenForDelete(passedUser);

  if (!idToken) {
    throw new Error("empty-id-token");
  }

  const response = await withTimeout(
    fetch(DELETE_ACCOUNT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        confirm: true,
      }),
    }),
    30000
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data?.ok === false) {
    throw new Error(data?.error || `delete-failed-${response.status}`);
  }

  return data;
};